import { Box } from "@mui/material";
import React, { useState, useId, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { List } from "./List";
import { ListNavigation } from "./ListNavigation";
import styles from "./MultiList.module.scss";
import { SearchBar } from "./SearchBar";

export const MultiList = ({
	initialDataset = null,
	datasets = {},
	globalHiddenFilters = [],
	showSearch = false,
	searchPlaceholder = "",
	showListSearch = false,
	variant = "button",
	disableSyncWithLocation = true,
	hideListIfEmpty = false,
	height = "none",
	locationPrefix = "/parliament",
	disablePushToHistory = false,
}) => {
	const id = useId();
	const navigate = useNavigate();
	const datasetKeys = useMemo(() => Object.keys(datasets), [datasets]);

	const { dataset = initialDataset || datasetKeys[0] } = useParams();
	const [searchParams] = useSearchParams();
	const [activeList, setActiveList] = useState(dataset);
	const [search, setSearch] = useState(searchParams.get("query"));
	const [globalFilters, setGlobalFilters] = useState(globalHiddenFilters);
	const [totals, setTotals] = useState(
		Object.fromEntries(datasetKeys.map((k) => [k, null])),
	);

	useEffect(() => {
		setActiveList(dataset);
	}, [dataset]);

	const handleSearchChange = useCallback((query) => {
		setSearch(query);
	}, []);

	const handleUpdateTotal = useCallback((dataset, total) => {
		setTotals((prevValues) => {
			return { ...prevValues, [dataset]: total };
		});
	}, []);

	const handleGlobalFilters = useCallback((filters) => {
		const newFilters = Object.assign({}, ...filters.map((f) => f.filter));
		setGlobalFilters(newFilters);
	}, []);

	const handleNavChange = useCallback(
		(list) => {
			setActiveList(list);
			if (!disableSyncWithLocation) {
				navigate(`${locationPrefix}/${list}`, {
					replace: disablePushToHistory,
				});
			}
		},
		[navigate, disableSyncWithLocation],
	);

	return (
		<>
			{activeList && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						height: height,
					}}
				>
					<Box
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 1,
							backgroundColor: "transparent",
							marginBottom: ".5em",
						}}
					>
						{showSearch && (
							<SearchBar
								placeholder={
									searchPlaceholder ||
									"Rechercher un sujet, un parlementaire avec @, un groupe avec /, un dossier avec #..."
								}
								onSearchChange={handleSearchChange}
								onUpdateGlobalFilters={handleGlobalFilters}
								className={styles.searchBar}
							/>
						)}
						<ListNavigation
							datasets={datasets}
							activeList={activeList}
							totals={totals}
							onNavChange={handleNavChange}
							variant={variant}
							hideIfEmpty={hideListIfEmpty}
						/>
					</Box>
					<Box
						sx={{
							flexGrow: 1,
							overflow: "auto",
						}}
					>
						{datasetKeys.map((dataset, index) => {
							return (
								<List
									key={`${dataset}-${id}`}
									id={`${dataset}-${id}`}
									dataset={datasets[dataset]}
									search={search}
									globalFilters={globalFilters}
									display={dataset === activeList}
									role="tabpanel"
									tabIndex={index}
									showSearch={
										datasets[dataset].showSearch === false
											? false
											: showListSearch
									}
									searchPlaceholder={datasets[dataset].searchPlaceholder}
									emptyText={datasets[dataset].emptyText}
									onUpdateTotal={handleUpdateTotal}
									disableSyncWithLocation={disableSyncWithLocation}
									perPageDefault={datasets[dataset].perPage}
								/>
							);
						})}
					</Box>
				</Box>
			)}
		</>
	);
};
